import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Table, Tag, Space, Button, Checkbox, Alert, Typography, Popconfirm, Progress, message, Spin } from 'antd'
import dayjs from 'dayjs'
import componentService, { componentLabel, errMsg } from '../../services/componentService'

const { Text } = Typography

export const BATCH_STATUS = {
  running: { label: '进行中', color: 'processing' },
  paused: { label: '已暂停', color: 'warning' },
  completed: { label: '已完成', color: 'success' },
  cancelled: { label: '已取消', color: 'default' },
  failed: { label: '失败', color: 'error' },
}

export const BATCH_PHASE = {
  canary: '灰度',
  soak: '观察',
  rolling: '滚动',
}

const NODE_STATE = {
  pending: { label: '等待', color: 'default' },
  inflight: { label: '更新中', color: 'processing' },
  done: { label: '完成', color: 'success' },
  failed: { label: '失败', color: 'error' },
  skipped: { label: '跳过', color: 'default' },
  waiting_idle: { label: '等空闲', color: 'warning' },
}

const POLL_MS = 3000
const MAX_FAILS = 5

const BatchProgressModal = ({ batchId, open, onClose, onChanged }) => {
  const [data, setData] = useState(null)
  const [retryFailed, setRetryFailed] = useState(false)
  const [acting, setActing] = useState(false)
  const [pollKey, setPollKey] = useState(0)
  const timerRef = useRef(null)
  const failRef = useRef(0)

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const fetchOnce = useCallback(async () => {
    try {
      const res = await componentService.getBatch(batchId)
      failRef.current = 0
      setData(res.data)
      if (res.data?.batch?.status !== 'running') stop()
    } catch (e) {
      failRef.current += 1
      if (failRef.current >= MAX_FAILS) {
        stop()
        message.error(`获取批次进度失败: ${errMsg(e)}`)
      }
    }
  }, [batchId])

  useEffect(() => {
    if (!open || !batchId) return undefined
    failRef.current = 0
    stop()
    fetchOnce()
    timerRef.current = setInterval(fetchOnce, POLL_MS)
    return stop
  }, [open, batchId, pollKey, fetchOnce])

  useEffect(() => {
    if (!open) {
      setData(null)
      setRetryFailed(false)
    }
  }, [open])

  const act = async (fn, okMsg) => {
    setActing(true)
    try {
      const res = await fn()
      setData(res.data)
      message.success(okMsg)
      setPollKey((k) => k + 1)
      onChanged && onChanged()
    } catch (e) {
      message.error(errMsg(e))
    } finally {
      setActing(false)
    }
  }

  const batch = data?.batch
  const st = batch ? BATCH_STATUS[batch.status] || { label: batch.status, color: 'default' } : null
  const active = batch && (batch.status === 'running' || batch.status === 'paused')

  const columns = [
    {
      title: '节点',
      dataIndex: 'node_name',
      render: (v, r) => (
        <Space size={4}>
          <span>{v || `#${r.node_id}`}</span>
          {r.canary && <Tag color="purple">灰度</Tag>}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'state',
      width: 90,
      render: (v) => {
        const s = NODE_STATE[v] || { label: v, color: 'default' }
        return <Tag color={s.color}>{s.label}</Tag>
      },
    },
    {
      title: '进度',
      width: 220,
      render: (_, r) => {
        const t = r.task
        const text = r.gate_reason || t?.error || t?.message
        return (
          <div>
            {t && r.state === 'inflight' && <Progress percent={t.progress || 0} size="small" />}
            {text && (
              <Text type={r.state === 'failed' ? 'danger' : 'secondary'} style={{ fontSize: 12 }} ellipsis={{ tooltip: text }}>
                {text}
              </Text>
            )}
          </div>
        )
      },
    },
  ]

  const footer = active ? (
    <Space>
      {batch.status === 'paused' && (
        <>
          <Checkbox checked={retryFailed} onChange={(e) => setRetryFailed(e.target.checked)}>
            重试失败
          </Checkbox>
          <Button
            type="primary"
            loading={acting}
            onClick={() => act(() => componentService.resumeBatch(batch.id, retryFailed), '已继续')}
          >
            继续
          </Button>
        </>
      )}
      <Popconfirm title="取消该批次?" onConfirm={() => act(() => componentService.cancelBatch(batch.id), '已取消')}>
        <Button danger loading={acting}>取消批次</Button>
      </Popconfirm>
      <Button onClick={onClose}>关闭</Button>
    </Space>
  ) : (
    <Button onClick={onClose}>关闭</Button>
  )

  return (
    <Modal
      title={batch ? `批量更新 #${batch.id} · ${componentLabel(batch.component)} ${batch.version || ''}` : '批量更新'}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={720}
      destroyOnClose
    >
      {!data ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={st.color}>{st.label}</Tag>
            {batch.status === 'running' && batch.phase && <Tag color="blue">{BATCH_PHASE[batch.phase] || batch.phase}</Tag>}
            {batch.phase === 'soak' && batch.soak_until && (
              <Text type="secondary" style={{ fontSize: 12 }}>观察至 {dayjs(batch.soak_until).format('HH:mm:ss')}</Text>
            )}
            {batch.force && <Tag color="red">强制</Tag>}
            {Object.entries(data.counts || {}).map(([k, n]) => (
              <Text key={k} type="secondary" style={{ fontSize: 12 }}>
                {(NODE_STATE[k] || { label: k }).label} {n}
              </Text>
            ))}
          </Space>
          {batch.error && <Alert type="error" showIcon message={batch.error} style={{ marginBottom: 12 }} />}
          <Table
            size="small"
            rowKey="node_id"
            columns={columns}
            dataSource={data.nodes || []}
            pagination={false}
            scroll={{ y: 420 }}
          />
        </>
      )}
    </Modal>
  )
}

export default BatchProgressModal
